import "../src/globals.css";
import { useEffect, useRef, useState } from "react";
import { Stack, usePathname, useRouter } from "expo-router";
import { Platform, View } from "react-native";
import { QueryClientProvider } from "@tanstack/react-query";
import { SheetProvider } from "react-native-actions-sheet";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { StatusBar } from "expo-status-bar";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { KeyboardProvider } from "react-native-keyboard-controller";
import { useFonts } from "expo-font";
import * as SplashScreen from "expo-splash-screen";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider, useI18n } from "@/context/I18nContext";
import { ToastProvider } from "@/context/ToastContext";
import { AppText } from "@/components/ui/AppText";
import { queryClient } from "@/lib/queryClient";
import { registerTokenAccessor } from "@/lib/api/client";
import { Button } from "@/components/ui/Button";
import { DropdownMenu } from "@/components/ui/DropdownMenu";
import { Icon } from "@/components/ui/Icon";
import { Ionicons } from "@expo/vector-icons";
import { initSentry, wrapApp } from "@/lib/sentry";

// Init Sentry as early as possible so unhandled errors during boot are
// captured. No-op when EXPO_PUBLIC_SENTRY_DSN is unset.
initSentry();

SplashScreen.preventAutoHideAsync();

function TokenRegistrar() {
  const { token } = useAuth();
  useEffect(() => {
    registerTokenAccessor(() => token);
  }, [token]);
  return null;
}

function DevShowcaseLink() {
  const router = useRouter();
  const pathname = usePathname();

  if (!__DEV__ || pathname.startsWith("/showcase")) {
    return null;
  }

  return (
    <View className="absolute end-4 bottom-28 z-50">
      <Button
        className="rounded-full"
        variant="tertiary"
        iconOnly
        accessibilityLabel="ui.login.showcaseA11y"
        icon={<AppText className="text-2xl">🎨</AppText>}
        onPress={() => router.push("/showcase")}
      />
    </View>
  );
}

function ChangeLanguage() {
  const { locale, setLocale, availableLocales } = useI18n();

  return (
    <View className="absolute end-4 bottom-44 z-50">
      <DropdownMenu
        sections={[
          {
            items: availableLocales.map((loc) => ({ value: loc.locale, label: loc.label })),
          },
        ]}
        value={locale}
        onSelect={setLocale}
        panelWidth={176}
        trigger={
          <View className="bg-stroke-30 rounded-full w-11 h-11 items-center justify-center">
            <Icon name="language" as={Ionicons} size={18} className="text-dark-text-50" />
          </View>
        }
      />
    </View>
  );
}

/**
 * Wraps the navigable content and applies a remount key whenever the RTL
 * direction flips on native. Two mechanisms ensure instant switching:
 *
 * 1. `style={{ direction }}` on the root View — this is a per-view prop that
 *    cascades to all children and takes effect immediately, unlike
 *    `I18nManager.forceRTL()` which only affects newly-created native views.
 *
 * 2. Changing the `key` forces React Native to destroy and recreate the entire
 *    subtree so components that internally read `I18nManager.isRTL` (e.g.
 *    TextInput, FlatList) also pick up the new direction.
 *
 * On web the direction is applied via `document.documentElement.dir` (handled
 * in I18nContext) so neither mechanism is needed there.
 */
function AppContent() {
  const { isRTL: rtl } = useI18n();
  const [rtlKey, setRtlKey] = useState(0);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (Platform.OS !== "web") {
      setRtlKey((k) => k + 1);
    }
  }, [rtl]);

  return (
    <View key={rtlKey} style={{ flex: 1, direction: rtl ? "rtl" : "ltr" }}>
      <Stack screenOptions={{ headerShown: false }} />
      <DevShowcaseLink />
      <ChangeLanguage />
    </View>
  );
}

function RootLayout() {
  const [fontsLoaded, fontError] = useFonts({
    // Inter Display — English and Chinese
    "InterDisplay-Regular": require("../assets/fonts/InterDisplay-Regular.ttf"),
    "InterDisplay-Medium": require("../assets/fonts/InterDisplay-Medium.ttf"),
    "InterDisplay-SemiBold": require("../assets/fonts/InterDisplay-SemiBold.ttf"),
    "InterDisplay-Bold": require("../assets/fonts/InterDisplay-Bold.ttf"),
    // Saudi — Arabic
    "Saudi-Regular": require("../assets/fonts/Saudi-Regular.ttf"),
    "Saudi-Medium": require("../assets/fonts/Saudi-Medium.ttf"),
    "Saudi-SemiBold": require("../assets/fonts/Saudi-SemiBold.ttf"),
    "Saudi-Bold": require("../assets/fonts/Saudi-Bold.ttf"),
  });

  useEffect(() => {
    if (fontsLoaded || fontError) {
      SplashScreen.hideAsync();
    }
  }, [fontsLoaded, fontError]);

  if (!fontsLoaded && !fontError) {
    return null;
  }

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <KeyboardProvider>
        <SheetProvider>
          <SafeAreaProvider>
            <QueryClientProvider client={queryClient}>
              <AuthProvider>
                <I18nProvider>
                  <ToastProvider>
                    <TokenRegistrar />
                    <StatusBar style="auto" />
                    <AppContent />
                  </ToastProvider>
                </I18nProvider>
              </AuthProvider>
            </QueryClientProvider>
          </SafeAreaProvider>
        </SheetProvider>
      </KeyboardProvider>
    </GestureHandlerRootView>
  );
}

export default wrapApp(RootLayout);
